import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Container, Row, Col, Card, Spinner } from "react-bootstrap";
import { supabase } from "../../config/Supabase";
import "../../styles/HerbCategories.css";
import SEO from "../utils/SEO";
import { getHerbCategory } from "../../data/herbCategories";
import { herbSlug } from "../../utils/herbSlug";

const truncate = (t, n) => (t && t.length > n ? `${t.slice(0, n)}…` : t || "");

// Herbs that support a given body-system category. Matches the category's
// keywords (ILIKE) against each herb's organs_systems_affected in Supabase.
const HerbCategoryList = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const category = getHerbCategory(slug);
  const [herbs, setHerbs] = useState([]);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    if (!category) {
      setStatus("notfound");
      return;
    }
    let active = true;
    (async () => {
      setStatus("loading");
      // Mirror the mobile app: match herbs whose therapeutic_actions overlap the
      // category's subcategories (lowercased — DB stores lowercase actions).
      const subs = (category.subcategories || []).map((s) => s.toLowerCase());
      const { data, error } = await supabase
        .from("herbs")
        .select("id,title,summary,img_url,scientific_name")
        .overlaps("therapeutic_actions", subs)
        .order("popularity_score", { ascending: false, nullsFirst: false })
        .limit(120);
      if (!active) return;
      if (error) {
        console.error("Error loading category herbs:", error);
        setStatus("error");
        return;
      }
      setHerbs(data || []);
      setStatus("ok");
    })();
    return () => {
      active = false;
    };
  }, [slug, category]);

  if (!category) {
    return (
      <Container style={{ padding: "140px 20px", textAlign: "center" }}>
        <h1>Category not found</h1>
        <p>
          Back to <Link to="/herbCategories">herb categories</Link>.
        </p>
      </Container>
    );
  }

  return (
    <Container style={{ paddingTop: "90px", paddingBottom: "70px" }}>
      <SEO
        title={`${category.name} Herbs - Sarv Herb Library`}
        description={category.description}
        canonicalPath={`/herbs/category/${category.slug}`}
        keywords={`${category.name} herbs, herbs for ${category.name.toLowerCase()}, natural remedies`}
      />
      <nav
        className="article-page__breadcrumb herbcat-breadcrumb"
        aria-label="breadcrumb"
      >
        <Link to="/">Home</Link> › <Link to="/herbCategories">Herbs</Link> ›{" "}
        <span>{category.name}</span>
      </nav>
      <h1 className="article-categoories-title herbcat-title">{category.name}</h1>
      <p className="herbcat-intro">{category.description}</p>

      {status === "loading" ? (
        <div className="text-center" style={{ padding: "80px 0" }}>
          <Spinner animation="border" />
        </div>
      ) : herbs.length === 0 ? (
        <p className="text-center" style={{ color: "#777", padding: "60px 0" }}>
          No herbs found in this category yet.{" "}
          <Link to="/herbCategories">Browse other categories →</Link>
        </p>
      ) : (
        <Row xs={1} sm={2} lg={3} className="g-4 mt-2">
          {herbs.map((h) => (
            <Col key={h.id}>
              <Card
                className="herbcat-card"
                role="button"
                onClick={() => navigate(`/herbs/${herbSlug(h)}`)}
              >
                <div className="herbcat-card__imgwrap">
                  <Card.Img variant="top" src={h.img_url} alt={h.title} loading="lazy" />
                </div>
                <Card.Body>
                  <Card.Title className="herbcat-card__title">{h.title}</Card.Title>
                  {h.scientific_name && (
                    <div className="herbcat-card__sci">{h.scientific_name}</div>
                  )}
                  <Card.Text className="herbcat-card__desc">{truncate(h.summary, 110)}</Card.Text>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )}
    </Container>
  );
};

export default HerbCategoryList;
